import React from 'react'
import styled from 'styled-components'
import { Search, BookOpen } from 'lucide-react'
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  fadeInLeft,
  fadeInRight,
  staggerContainerFast,
  smoothEase,
} from './animations/AnimatedSection'

const listItemVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: smoothEase },
  },
}

const ResearchRepositorySection = () => {
  return (
    <Section>
      <Container>
        {/* Left Side: UI Card */}
        <LeftColumn>
          <AnimatedSection variants={fadeInLeft} threshold={0.2}>
            <OuterCard>
              <InnerContent>
                {/* Search Bar */}
                <SearchBar>
                  <Search size={20} color="#919191" />
                  <SearchInputText>Search climate research...</SearchInputText>
                </SearchBar>

                {/* Items List */}
                <StaggerContainer
                  variants={staggerContainerFast}
                  style={{
                    display: 'flex',
                    flexDirection: 'column' as const,
                    alignItems: 'flex-start',
                    gap: '12px',
                    width: '100%',
                  }}
                >
                  {/* Item 1 */}
                  <StaggerItem
                    variants={listItemVariants}
                    style={{ width: '100%' }}
                  >
                    <ResultItem bg="#FDE9EA">
                      <ItemHeader>
                        <ItemTitle>🌊 Ocean Conservation</ItemTitle>
                        <Tag bg="#BBE148" color="#1B3C19">
                          Verified
                        </Tag>
                      </ItemHeader>
                      <ItemDescription>
                        Impact of plastic pollution on marine ecosystems..
                      </ItemDescription>
                      <MetaRow>
                        <MetaItem>
                          <BookOpen size={12} color="#2A2A2A" />
                          245 reads
                        </MetaItem>
                        <MetaItem>Updated 2 days ago</MetaItem>
                      </MetaRow>
                    </ResultItem>
                  </StaggerItem>

                  {/* Item 2 */}
                  <StaggerItem
                    variants={listItemVariants}
                    style={{ width: '100%' }}
                  >
                    <ResultItem bg="#E9F5ED">
                      <ItemHeader>
                        <ItemTitle>🌱 Sustainable Farming</ItemTitle>
                        <Tag bg="#BBE148" color="#1B3C19">
                          Verified
                        </Tag>
                      </ItemHeader>
                      <ItemDescription>
                        Regenerative agriculture practices for soil health..
                      </ItemDescription>
                      <MetaRow>
                        <MetaItem>
                          <BookOpen size={12} color="#2A2A2A" />
                          189 reads
                        </MetaItem>
                        <MetaItem>Updated 5 days ago</MetaItem>
                      </MetaRow>
                    </ResultItem>
                  </StaggerItem>

                  {/* Item 3 */}
                  <StaggerItem
                    variants={listItemVariants}
                    style={{ width: '100%' }}
                  >
                    <ResultItem bg="#E8F7FB">
                      <ItemHeader>
                        <ItemTitle>☀️ Solar Energy</ItemTitle>
                        <Tag bg="#F2C849" color="#2A2A2A">
                          New
                        </Tag>
                      </ItemHeader>
                      <ItemDescription>
                        Solar panel efficiency in urban environments...
                      </ItemDescription>
                      <MetaRow>
                        <MetaItem>
                          <BookOpen size={12} color="#2A2A2A" />
                          92 reads
                        </MetaItem>
                        <MetaItem>Updated 1 days ago</MetaItem>
                      </MetaRow>
                    </ResultItem>
                  </StaggerItem>
                </StaggerContainer>
              </InnerContent>
            </OuterCard>
          </AnimatedSection>
        </LeftColumn>

        {/* Right Side: Text Highlights */}
        <RightColumn>
          <AnimatedSection variants={fadeInRight} threshold={0.3}>
            <TextWrapper>
              <SectionLabel>Research Repository</SectionLabel>
              <SectionTitle>AI-Powered Research Repository</SectionTitle>
              <SectionSubtitle>
                Access our comprehensive research repository powered by AI.
                Search through SDG-focused studies, global challenges research,
                and sustainability reports with intelligent semantic search.
              </SectionSubtitle>
            </TextWrapper>
          </AnimatedSection>
        </RightColumn>
      </Container>
    </Section>
  )
}

const Section = styled.section`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0 120px;
  width: 100%;
  background: url(/images/paperboard-texture.webp), #f8f7f0;
  background-blend-mode: multiply, normal;
  background-size: cover;
  overflow: hidden;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  max-width: 1440px;
  gap: 40px;
  padding-left: 100px;

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: center;
    padding: 0 24px;
    gap: 60px;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  max-width: 584px;

  @media (max-width: 1100px) {
    max-width: 100%;
  }
`

const OuterCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 32px;
  width: 100%;
  max-width: 584px;
  background: #ffffff;
  border-width: 1px 4px 4px 1px;
  border-style: solid;
  border-color: rgba(42, 42, 42, 0.1);
  box-shadow: 0px 32px 64px -12px rgba(0, 0, 0, 0.1);
  border-radius: 24px;
`

const InnerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`

const SearchBar = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px 18px;
  gap: 12px;
  width: 100%;
  background: #ffffff;
  border: 1.5px solid rgba(222, 222, 222, 0.6);
  border-radius: 12px;
  box-shadow: inset 0px 2px 4px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #bbe148;
    box-shadow: 0px 0px 0px 4px rgba(187, 225, 72, 0.2);
  }
`

const SearchInputText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.04em;
  color: #919191;
`

const ResultItem = styled.div<{ bg: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  width: 100%;
  background: ${(props) => props.bg};
  border-width: 1px;
  border-style: solid;
  border-color: rgba(42, 42, 42, 0.1);
  border-radius: 12px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0px 8px 16px -4px rgba(0, 0, 0, 0.05);
    border-color: rgba(42, 42, 42, 0.2);
  }
`

const ItemHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`

const ItemTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.04em;
  color: #2a2a2a;
  margin: 0;
`

const Tag = styled.span<{ bg: string; color: string }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 11px;
  background: ${(props) => props.bg};
  border-radius: 100px;
  font-family: 'Roboto', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.color};
`

const ItemDescription = styled.p`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: -0.04em;
  color: #2a2a2a;
  margin: 0;
  margin-top: -4px;
`

const MetaRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`

const MetaItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: -0.04em;
  color: #2a2a2a;
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 40px;
  width: 100%;
  max-width: 538px;
  min-height: 465px;
  background: url(/images/paperboard-texture.webp), #bbe148;
  background-blend-mode: multiply, normal;
  border-radius: 24px 0px 0px 24px;
  margin-top: 200px;

  @media (max-width: 1100px) {
    border-radius: 24px;
    max-width: 100%;
    padding: 60px 24px;
    margin-top: 0;
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 458px;
`

const SectionLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  text-align: center;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  color: #2a2a2a;
  margin: 0;
`

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 48px;
  line-height: 56px;
  letter-spacing: -0.04em;
  text-align: center;
  color: #000000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }
`

const SectionSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  text-align: center;
  color: rgba(42, 42, 42, 0.85);
  margin: 0;
  margin-top: 16px;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`

export default ResearchRepositorySection
